// Image optimization utilities for responsive images

export const generateImageSrcSet = (baseUrl: string, sizes: Array<{ width: number; height?: number }>) => {
  return sizes
    .map(size => {
      const params = new URLSearchParams();
      params.set('w', size.width.toString());
      if (size.height) {
        params.set('h', size.height.toString());
        params.set('fit', 'crop');
      }
      return `${baseUrl}?${params.toString()} ${size.width}w`;
    })
    .join(', ');
};

export const getOptimizedImageUrl = (baseUrl: string, width: number, height?: number, format?: 'webp' | 'origin') => {
  // Check if URL already has query params
  const hasParams = baseUrl.includes('?');
  const separator = hasParams ? '&' : '?';
  
  const params = new URLSearchParams();
  params.set('w', width.toString());
  if (height) {
    params.set('h', height.toString());
    params.set('fit', 'crop');
  }
  
  // For WebP optimization, try to use WebP version if available
  if (format === 'webp' && !baseUrl.includes('.webp')) {
    // For Supabase Storage, we'll rely on the picture element fallback
    // as Supabase doesn't support format conversion via query params
  }
  
  return `${baseUrl}${separator}${params.toString()}`;
};

// Pre-defined sizes for common image types
export const IMAGE_SIZES = {
  // Course icons (96x96px display - optimized from 90x90)
  courseIcon: [
    { width: 96, height: 96 },
    { width: 192, height: 192 } // 2x for retina
  ],
  
  // News thumbnails (378x192px display)
  newsThumb: [
    { width: 378, height: 192 },
    { width: 756, height: 384 } // 2x for retina
  ],
  
  // Course detail images (512px max)
  courseDetail: [
    { width: 256, height: 256 },
    { width: 512, height: 512 },
    { width: 768, height: 768 }
  ],
  
  // News detail images (896x384px display)
  newsDetail: [
    { width: 448, height: 192 },
    { width: 896, height: 384 }
  ],
  
  // Header logo (168x40px display)  
  logo: [
    { width: 168, height: 40 },
    { width: 336, height: 80 } // 2x for retina
  ]
};

export const createResponsiveImageProps = (
  src: string,
  alt: string,
  type: keyof typeof IMAGE_SIZES,
  className?: string,
  loading: 'lazy' | 'eager' = 'lazy'
) => {
  const sizes = IMAGE_SIZES[type];
  const primarySize = sizes[0];
  
  return {
    src: getOptimizedImageUrl(src, primarySize.width, primarySize.height),
    srcSet: generateImageSrcSet(src, sizes),
    alt,
    className,
    loading,
    decoding: 'async' as const,
    width: primarySize.width,
    height: primarySize.height,
    sizes: `${primarySize.width}px`
  };
};